import { useTranslation } from 'react-i18next';
import { DecisionLogEditor } from './DecisionLogEditor.js';
import { EditableSection } from './EditableSection.js';
import { FormatHint } from './FormatHint.js';
import { SpecTable } from './SpecTable.js';
import type { SpecDecisionLog, SpecDecisionRow } from '../../../lib/api/spec-requirements.js';

export interface DecisionLogSectionProps {
  /** 今保存されている決定記録。無い案件では null */
  decisionLog: SpecDecisionLog | null;
  /** この節を開いているか */
  editing: boolean;
  /** 直前の保存で残った注意（捨てた案が無い行） */
  warnings: string[];
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 編集を開く */
  onEdit: () => void;
  /** 保存する */
  onSave: (rows: SpecDecisionRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** レビュー操作。呼び出し側が組み立てるので、ここでは `ReviewActions` を知らない */
  actions: React.ReactNode;
}

/**
 * 決定記録の節。読むときは表、編集するときはフォーム。
 *
 * 他の 4 つの成果物は「決まったこと」しか持てないので、選ばなかった案はここにしか残らない。
 * 決まっていないものはナラティブの「まだ決まっていないこと」に置いたままにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 決定記録（決めること・決めたこと・選ばなかった案・なぜ）を読み、編集して保存し、レビュー操作を並べる節
 * @graph-connects DecisionLogEditor [calls] 決定記録の編集
 */
export function DecisionLogSection({
  decisionLog,
  editing,
  warnings,
  saving,
  error,
  onEdit,
  onSave,
  onCancel,
  actions,
}: DecisionLogSectionProps) {
  const { t } = useTranslation();

  return (
    <EditableSection
      heading={t('spec.artifact.decisionLog')}
      action={t(decisionLog === null ? 'spec.action.write' : 'spec.action.edit')}
      editing={editing}
      warnings={warnings}
      onEdit={onEdit}
    >
      {editing ? (
        <FormatHint artifact="decision_log" label={t('spec.artifact.decisionLog')} />
      ) : null}
      {editing ? (
        <DecisionLogEditor
          decisionLog={decisionLog}
          saving={saving}
          error={error}
          warnings={warnings}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : decisionLog === null ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.decisionLog.empty')}</p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文の行が返る。空で隠されていないので、
            読んでいるものが原文だと分かるように断る */}
          {decisionLog?.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.decisionLog.untranslated')}
            </p>
          ) : null}
          <SpecTable
            headings={[
              t('spec.decisionLog.columns.topic'),
              t('spec.decisionLog.columns.decision'),
              t('spec.decisionLog.columns.rejected'),
              t('spec.decisionLog.columns.why'),
            ]}
            rows={decisionLog.rows.map((row) => ({
              key: `${row.topic}/${row.decision}`,
              cells: [row.topic, row.decision, row.rejected, row.why],
            }))}
          />
        </>
      )}
      {actions}
    </EditableSection>
  );
}
